import type { TaxIdType } from "./types";

const EIN_PATTERN = /^\d{2}-\d{7}$/;
const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/;
const ITIN_PATTERN = /^9\d{2}-\d{2}-\d{4}$/;

const FTIN_MIN_LENGTH = 5;
const FTIN_MAX_LENGTH = 20;

const TAX_ID_TYPES: readonly TaxIdType[] = ["ssn", "ein", "itin", "ftin"];

/** EIN format: XX-XXXXXXX. */
export function isValidEIN(value: string): boolean {
  if (typeof value !== "string") return false;
  return EIN_PATTERN.test(value.trim());
}

/**
 * Coerce a persisted tax-id type (`'SSN'`, `'ssn'`, …) to the canonical
 * lowercase union. Returns null for anything unrecognised.
 */
export function normalizeTaxIdType(
  value: string | null | undefined,
): TaxIdType | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return TAX_ID_TYPES.includes(normalized as TaxIdType)
    ? (normalized as TaxIdType)
    : null;
}

export const SSN_AREA_MESSAGE =
  "This SSN cannot be valid: SSNs never start with 000, 666 or 9 (a number starting with 9 is an ITIN)";

export const ITIN_GROUP_MESSAGE =
  "That doesn't look like a valid ITIN. Check the number on your IRS ITIN letter. No ITIN? Choose FTIN.";

/** The FTIN format message names the field in plain words rather than "FTIN must be in format". */
const FTIN_FORMAT_MESSAGE = `Enter your foreign tax ID, ${FTIN_MIN_LENGTH} to ${FTIN_MAX_LENGTH} characters.`;

const TAX_ID_TYPE_MESSAGE = "Tax ID type must be SSN, EIN, ITIN, or FTIN";

/** SSA never issues area 000, 666 or 900-999 (the 9xx block belongs to ITINs). */
function hasIssuableSsnArea(ssn: string): boolean {
  const area = ssn.slice(0, 3);
  return area !== "000" && area !== "666" && !area.startsWith("9");
}

/** IRS ITIN group (digits 4-5): 50-65, 70-88, 90-92, 94-99. */
function hasIssuableItinGroup(itin: string): boolean {
  const group = Number(itin.slice(4, 6));
  return (
    (group >= 50 && group <= 65) ||
    (group >= 70 && group <= 88) ||
    (group >= 90 && group <= 92) ||
    (group >= 94 && group <= 99)
  );
}

// Foreign IDs vary too much for an allowlist (a Mexican RFC has "&" and
// "Ñ"), but none carries control characters or markup.
// eslint-disable-next-line no-control-regex
const FTIN_FORBIDDEN = /[\u0000-\u001f\u007f<>"`]/;

function isWellFormed(value: string, type: TaxIdType): boolean {
  switch (type) {
    case "ssn":
      return SSN_PATTERN.test(value);
    case "ein":
      return isValidEIN(value);
    case "itin":
      return ITIN_PATTERN.test(value);
    case "ftin":
      return (
        value.length >= FTIN_MIN_LENGTH &&
        value.length <= FTIN_MAX_LENGTH &&
        !FTIN_FORBIDDEN.test(value)
      );
    default:
      return false;
  }
}

/**
 * Why a tax ID cannot be right for its declared type, or null when it can.
 * Format first, then the issuance rules (V4). The message never carries the
 * value, so it is safe in a 400 body.
 */
export function taxIdIssue(
  value: string | null | undefined,
  declaredType: string | null | undefined,
): string | null {
  const type = normalizeTaxIdType(declaredType);
  if (type === null) return TAX_ID_TYPE_MESSAGE;
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!isWellFormed(trimmed, type)) {
    if (type === "ftin") return FTIN_FORMAT_MESSAGE;
    return `${type.toUpperCase()} must be in format ${describeTaxIdFormat(type)}`;
  }
  if (type === "ssn" && !hasIssuableSsnArea(trimmed)) return SSN_AREA_MESSAGE;
  if (type === "itin" && !hasIssuableItinGroup(trimmed))
    return ITIN_GROUP_MESSAGE;
  return null;
}

/** Validate a tax ID against its type's format and issuance rules. */
export function isValidTaxId(value: string, type: TaxIdType): boolean {
  if (typeof value !== "string") return false;
  return taxIdIssue(value, type) === null;
}

/**
 * Comparison key for duplicate detection: strips punctuation and case so
 * `123-45-6789` and `123456789` collide.
 */
export function normalizeTaxIdForComparison(value: string): string {
  if (typeof value !== "string") return "";
  return value.replace(/[^0-9a-zA-Z]/g, "").toUpperCase();
}

/** Human-facing description of the format a type expects. */
export function describeTaxIdFormat(type: TaxIdType): string {
  switch (type) {
    case "ssn":
      return "XXX-XX-XXXX";
    case "ein":
      return "XX-XXXXXXX";
    case "itin":
      return "9XX-XX-XXXX";
    case "ftin":
      return `${FTIN_MIN_LENGTH}-${FTIN_MAX_LENGTH} characters`;
    default:
      return "a valid format";
  }
}
